import { useEffect, useState } from "react";
import { Moon, Sparkles, Sunrise, Sunset } from "lucide-react";
import { API } from "@/lib/api";

/**
 * Live moon-phase panel — sits under the Schumann + Cosmic strip on the
 * dashboard. Renders the moon's illuminated crescent as SVG (no fonts,
 * always crisp) and includes an effects paragraph tuned for sensitives.
 */
export default function MoonPanel() {
  const [m, setM] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await fetch(`${API}/moon/current`);
        const data = await res.json();
        if (alive) setM(data);
      } catch { /* leave as null */ }
    };
    load();
    const id = setInterval(load, 15 * 60 * 1000); // refresh every 15 min
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!m) {
    return (
      <div className="parchment-card rounded-2xl p-8 md:p-10 h-full">
        <div className="label-overline">Moon phase</div>
        <div className="text-[color:var(--text-muted)] mt-3">Reading the sky…</div>
      </div>
    );
  }

  return (
    <div className="parchment-card rounded-2xl p-8 md:p-10 h-full hover-lift" data-testid="moon-panel">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="label-overline constellation-underline">Moon phase</div>
          <h2 className="mt-3 font-serif-display text-white text-2xl md:text-3xl tracking-tight">
            {m.phase}
          </h2>
          <p className="font-serif-italic text-[color:var(--text-secondary)] mt-1">
            {m.moon_sign_glyph} in {m.moon_sign} · {m.waxing ? "waxing" : "waning"}
          </p>
        </div>
        <div
          className="px-3 py-1 rounded-full border font-serif-italic text-sm capitalize"
          style={{ borderColor: "var(--rose-gold)", color: "var(--rose-gold)" }}
          data-testid="moon-illumination-badge"
        >
          {m.illumination_pct}% lit
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-8 items-center">
        {/* SVG moon illustration */}
        <div className="md:col-span-2 flex items-center justify-center">
          <MoonGlyph illumination={m.illumination_pct} waxing={m.waxing} />
        </div>

        {/* Stats + effects */}
        <div className="md:col-span-3 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <MiniStat label="Age" value={`${m.age_days} days`} sub="of a 29.5-day cycle" />
            <MiniStat label="Sign" value={m.moon_sign} sub="the moon is passing through" />
            <MiniStat label="Next full moon" value={fmtWhen(m.next_full_moon)} sub="dial up your rest" testid="moon-next-full" />
            <MiniStat label="Next new moon" value={fmtWhen(m.next_new_moon)} sub="a clean slate" testid="moon-next-new" />
          </div>
          <p className="mt-2 font-serif-italic text-[color:var(--parchment-soft)] leading-relaxed text-sm" data-testid="moon-effects">
            {m.effects}
          </p>
        </div>
      </div>

      <div className="mt-6 pt-5 border-t border-[color:var(--gold)]/10 flex items-center justify-between label-overline">
        <span>Source · {m.source}</span>
        <span>refreshes every 15 min</span>
      </div>
    </div>
  );
}

// ─── SVG moon glyph — a real crescent, not an emoji ───
function MoonGlyph({ illumination, waxing }) {
  const size = 160;
  const r = size / 2 - 6;
  const cx = size / 2;
  const cy = size / 2;
  // Terminator ellipse width shrinks toward full (illumination=100) and grows toward new (illumination=0)
  const pct = Math.max(0, Math.min(100, illumination));
  const ellipseRx = r * Math.abs(1 - pct / 50); // 0 at full, r at new
  // If waxing, the illuminated side is on the right; if waning, on the left.
  const litOnRight = waxing;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <defs>
        <radialGradient id="moonlit" cx="50%" cy="45%" r="55%">
          <stop offset="0%" stopColor="#f6ecd4" />
          <stop offset="70%" stopColor="#e5d3a0" />
          <stop offset="100%" stopColor="#b8894a" />
        </radialGradient>
        <radialGradient id="moonshadow" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#0a1128" />
          <stop offset="100%" stopColor="#050917" />
        </radialGradient>
        <filter id="moonglow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="4" />
        </filter>
      </defs>
      {/* Outer glow */}
      <circle cx={cx} cy={cy} r={r + 4} fill="#d4a24c" opacity="0.18" filter="url(#moonglow)" />
      {/* Base lit disc */}
      <circle cx={cx} cy={cy} r={r} fill="url(#moonlit)" stroke="#d4a24c55" strokeWidth="0.75" />
      {/* Shadow overlay: half-disc + ellipse (the terminator) */}
      <g>
        {/* half-disc: covers the un-lit side */}
        <path
          d={
            litOnRight
              // waxing: shadow on LEFT half of moon (from top to bottom on left semicircle)
              ? `M ${cx} ${cy - r} A ${r} ${r} 0 0 0 ${cx} ${cy + r} Z`
              // waning: shadow on RIGHT half
              : `M ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${cx} ${cy + r} Z`
          }
          fill="url(#moonshadow)"
        />
        {/* Terminator ellipse — subtractive/additive depending on whether we're past half */}
        <ellipse
          cx={cx}
          cy={cy}
          rx={ellipseRx}
          ry={r}
          fill={pct > 50 ? "url(#moonlit)" : "url(#moonshadow)"}
        />
      </g>
      {/* Craters — decorative */}
      <g fill="#050917" opacity="0.14">
        <circle cx={cx - r * 0.28} cy={cy - r * 0.10} r={r * 0.10} />
        <circle cx={cx + r * 0.10} cy={cy + r * 0.30} r={r * 0.06} />
        <circle cx={cx + r * 0.32} cy={cy - r * 0.28} r={r * 0.07} />
        <circle cx={cx - r * 0.05} cy={cy - r * 0.32} r={r * 0.04} />
      </g>
    </svg>
  );
}

function MiniStat({ label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-[color:var(--gold)]/15 p-3 md:p-4" data-testid={testid}>
      <div className="label-overline text-[10px]">{label}</div>
      <div className="mt-1 font-serif-display text-[color:var(--parchment)] text-lg md:text-xl leading-tight">{value}</div>
      <div className="font-serif-italic text-[color:var(--text-muted)] text-xs">{sub}</div>
    </div>
  );
}

function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const days = Math.round((d - now) / 86400000);
  const hh = d.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  if (days === 0) return `today · ${hh.split(", ")[1] || hh}`;
  if (days === 1) return `tomorrow · ${hh.split(", ")[1] || hh}`;
  if (days < 0) return "just passed";
  return `${hh} · in ${days} d`;
}
